import { useState } from 'react'
import { Head, Link, router, usePoll } from '@inertiajs/react'
import { ArrowLeft, ArrowUpRight, Check, Clock3 } from 'lucide-react'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import { MiniAvatar } from '@/frontend/components/MiniAvatar'

interface Approval {
  id: string; title: string; status: string; requested_at: string; requester_name: string
  can_decide: boolean; project_path: string | null; approve_path: string; decline_path: string; snooze_path: string
  scope: {
    project_name: string; objective: string; handoff: string; source_refs: string[]
    team: { pm_worker_id: string; pm_name: string; agents: { id: string; name: string; role: string; description?: string }[] }
    execution: { status: string; error?: string }
  }
}
interface Copy {
  back: string; review: string; title: string; requested_by: string; objective: string; team: string
  pm: string; automatic: string; context: string; sources: string; approve: string; decline: string
  snooze: string; open: string; limits: string; states: Record<string, string>; execution: Record<string, string>
}

function ProjectLaunchApproval({ approval, copy }: { approval: Approval; copy: Copy }) {
  const [processing, setProcessing] = useState(false)
  usePoll(10_000, { only: ['approval'] })
  const scope = approval.scope
  const decide = (path: string) => {
    if (processing) return
    setProcessing(true)
    router.patch(path, {}, { preserveScroll: true, onFinish: () => setProcessing(false) })
  }
  return <main key={approval.id} className="mx-auto w-full max-w-3xl px-5 py-7 md:px-8 md:py-10">
    <Head title={approval.title} />
    <Link href="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground"><ArrowLeft className="size-4" />{copy.back}</Link>
    <header className="mb-8 mt-7 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{copy.requested_by} {approval.requester_name}</span>
        <span className="rounded-md bg-muted px-2 py-1" role="status">{copy.states[approval.status]}</span>
      </div>
      <h1 className="break-words text-2xl font-semibold tracking-tight">{scope.project_name}</h1>
      <p className="break-words text-base leading-relaxed text-muted-foreground">{scope.objective}</p>
    </header>
    <section className="border-y py-6">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">{copy.team}</h2>
        <span className="text-xs text-muted-foreground">{copy.pm}: <strong className="font-medium text-foreground">{scope.team.pm_name}</strong></span>
      </div>
      <ul className="grid gap-3 sm:grid-cols-2">
        {scope.team.agents.map(agent => <li key={agent.id} className="flex items-center gap-3 rounded-lg bg-muted/40 p-3">
          <MiniAvatar name={agent.name} workerId={agent.id} />
          <div className="min-w-0"><p className="truncate text-sm font-medium">{agent.name}</p><p className="text-xs text-muted-foreground">{agent.role}</p>{agent.description && <p className="mt-1 break-words text-xs leading-5 text-muted-foreground">{agent.description}</p>}</div>
          {agent.id === scope.team.pm_worker_id && <Check className="ml-auto size-4 shrink-0 text-success" />}
        </li>)}
      </ul>
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{copy.automatic}</p>
    </section>
    <details className="group border-b py-5">
      <summary className="cursor-pointer text-sm font-medium">{copy.context}</summary>
      <p className="mt-4 whitespace-pre-wrap break-words text-sm leading-relaxed text-muted-foreground">{scope.handoff}</p>
    </details>
    <details className="border-b py-5">
      <summary className="cursor-pointer text-sm font-medium">{copy.sources} <span className="ml-1 text-muted-foreground">{scope.source_refs.length}</span></summary>
      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">{scope.source_refs.map(ref => <li key={ref} className="break-words">
        {/^https?:\/\//i.test(ref) ? <a href={ref} target="_blank" rel="noreferrer" className="underline decoration-border underline-offset-4">{ref}</a> : ref}
      </li>)}</ul>
    </details>
    <p className="my-5 text-xs leading-relaxed text-muted-foreground">{copy.limits}</p>
    {approval.can_decide ? <div className="flex flex-wrap items-center gap-2">
      <button disabled={processing} onClick={() => decide(approval.approve_path)} className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50">{copy.approve}</button>
      <button disabled={processing} onClick={() => decide(approval.snooze_path)} className="inline-flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm text-muted-foreground disabled:opacity-50"><Clock3 className="size-4" />{copy.snooze}</button>
      <button disabled={processing} onClick={() => decide(approval.decline_path)} className="rounded-lg px-3 py-2.5 text-sm text-muted-foreground disabled:opacity-50">{copy.decline}</button>
    </div> : <div className="space-y-3">
      <p className="text-sm text-muted-foreground" role="status">{approval.status === 'declined' ? copy.states.declined : copy.execution[scope.execution.status]}</p>
      {scope.execution.error && <p className="text-sm text-destructive" role="alert">{scope.execution.error}</p>}
      {approval.project_path && <Link href={approval.project_path} className="inline-flex items-center gap-2 text-sm font-medium text-primary">{copy.open}<ArrowUpRight className="size-4" /></Link>}
    </div>}
  </main>
}
ProjectLaunchApproval.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>
export default ProjectLaunchApproval
